var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useTransitionStyles } from '@floating-ui/react';
import { arrow, autoUpdate, flip, hide, offset, shift, useFloating } from '@floating-ui/react';
import React, { useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { cssThemedValue, useDirection } from '@creditornot/cb-ingredients';
import { useLegacyPopoverState } from './hooks/useLegacyPopoverState';
import { useOnClickInside } from './hooks/useOnClickInside';
import { useOnClickOutside } from './hooks/useOnClickOutside';
import { PopoverBase } from './PopoverBase';
var POPOVER_OFFSET_PX = 4;
var ARROWUP_WIDTH_PX = 16;
var ARROWUP_HEIGHT_PX = ARROWUP_WIDTH_PX / 2;
var HOVER_AREA_WIDTH = ARROWUP_HEIGHT_PX + POPOVER_OFFSET_PX;
var Root = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: inline-block;\n"], ["\n  display: inline-block;\n"])));
var Content = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  min-height: 30px;\n  padding: ", ";\n  color: ", ";\n  border-radius: 8px;\n  box-sizing: border-box;\n"], ["\n  min-height: 30px;\n  padding: ", ";\n  color: ", ";\n  border-radius: 8px;\n  box-sizing: border-box;\n"])), function (_a) {
    var $padded = _a.$padded;
    return ($padded ? '8px' : '0px');
}, cssThemedValue('textPrimary'));
var isFloatingUiReferenceRegularElement = function (ref) {
    return Boolean(ref.current && typeof ref.current === 'object' && 'classList' in ref.current);
};
var getDirectionAwarePlacement = function (direction, placement) {
    if (direction === 'ltr') {
        return placement;
    }
    if (placement.includes('left')) {
        return placement.replace('left', 'right');
    }
    if (placement.includes('right')) {
        return placement.replace('right', 'left');
    }
    return placement;
};
var getClosedStyles = function (_a) {
    var side = _a.side;
    if (side === 'top') {
        return { opacity: 0, transform: 'translateY(-8px)' };
    }
    if (side === 'bottom') {
        return { opacity: 0, transform: 'translateY(8px)' };
    }
    if (side === 'left') {
        return { opacity: 0, transform: 'translateX(-8px)' };
    }
    if (side === 'right') {
        return { opacity: 0, transform: 'translateX(8px)' };
    }
    return {};
};
var getOpenedStyles = function (_a) {
    var side = _a.side;
    if (side === 'top' || side === 'bottom') {
        return { opacity: 1, transform: 'translateY(0px)' };
    }
    if (side === 'left' || side === 'right') {
        return { opacity: 1, transform: 'translateX(0px)' };
    }
    return {};
};
var globalMountElement = null;
var getGlobalMountElement = function () {
    if (globalMountElement) {
        return globalMountElement;
    }
    var mountElement = document && document.querySelector('.cb-popover-portal');
    if (mountElement) {
        globalMountElement = mountElement;
        return globalMountElement;
    }
    globalMountElement = document.createElement('div');
    globalMountElement.className = 'cb-popover-portal';
    document.body.append(globalMountElement);
    return globalMountElement;
};
// An extra hoverarea between the popover and the reference element to prevent
// the popover from closing when openOnHover=true and when moving cursor from
// inside the reference element to popover.
var HoverArea = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  position: absolute;\n  opacity: 0;\n"], ["\n  position: absolute;\n  opacity: 0;\n"])));
/**
 * @deprecated Use `Popover` instead.
 *
 * This is a legacy adapter for an old implementation of `Popover`. It implements:
 * * legacy props interface
 * * state handling
 * * wrapper element
 * * different defaults
 */
export var LegacyPopover = function (_a) {
    var _b;
    var _c = _a.boundariesElement, boundariesElement = _c === void 0 ? 'viewport' : _c, children = _a.children, className = _a.className, _d = _a.closeOnClickInside, closeOnClickInside = _d === void 0 ? false : _d, _e = _a.closeOnClickOutside, closeOnClickOutside = _e === void 0 ? true : _e, content = _a.content, contentZIndex = _a.contentZIndex, _f = _a.delayOnMouseEnter, delayOnMouseEnter = _f === void 0 ? 50 : _f, _g = _a.delayOnMouseLeave, delayOnMouseLeave = _g === void 0 ? 0 : _g, _h = _a.disableContentPadding, disableContentPadding = _h === void 0 ? false : _h, disabled = _a.disabled, fallbackPlacements = _a.fallbackPlacements, _j = _a.hideWhenOutOfView, hideWhenOutOfView = _j === void 0 ? true : _j, onClickInside = _a.onClickInside, onClickOutside = _a.onClickOutside, onCloseStart = _a.onCloseStart, onOpenStart = _a.onOpenStart, _k = _a.placement, placementProp = _k === void 0 ? 'auto' : _k, _l = _a.portalElement, portalElement = _l === void 0 ? getGlobalMountElement() : _l, _m = _a.positionStrategy, positionStrategy = _m === void 0 ? 'fixed' : _m, _o = _a.renderArrow, renderArrow = _o === void 0 ? true : _o, showProp = _a.show, _p = _a.showOnClick, showOnClick = _p === void 0 ? true : _p, _q = _a.showOnHover, showOnHover = _q === void 0 ? true : _q, style = _a.style;
    var direction = useDirection();
    var arrowTipRef = useRef(null);
    var controlledMode = showProp !== undefined;
    var popoverState = useLegacyPopoverState({
        closeOnClickInsidePopover: closeOnClickInside,
        closeOnClickOutsidePopoverAndReference: closeOnClickOutside,
        delayOnMouseEnter: delayOnMouseEnter,
        delayOnMouseLeave: delayOnMouseLeave,
        enabled: !controlledMode && !disabled,
        onCloseStart: onCloseStart,
        onOpenStart: onOpenStart,
        showOnClick: showOnClick,
        showOnHover: showOnHover,
    });
    var show = controlledMode ? showProp : popoverState.popoverHandlers.show;
    var handleClickInside = function (event) {
        var _a, _b;
        if (!controlledMode && !disabled) {
            (_b = (_a = popoverState.popoverHandlers).onClickInside) === null || _b === void 0 ? void 0 : _b.call(_a, event);
        }
        onClickInside === null || onClickInside === void 0 ? void 0 : onClickInside(event);
    };
    var handleClickOutsideContentAndPositionRef = function (event) {
        var _a, _b;
        if (!controlledMode && !disabled) {
            (_b = (_a = popoverState.popoverHandlers).onClickOutsideContentAndPositionRef) === null || _b === void 0 ? void 0 : _b.call(_a, event);
        }
        if (show) {
            onClickOutside === null || onClickOutside === void 0 ? void 0 : onClickOutside(event);
        }
    };
    var renderProps = useMemo(function () { return ({
        close: popoverState.close,
        open: popoverState.open,
    }); }, [popoverState.close, popoverState.open]);
    var _r = useFloating({
        open: show,
        placement: getDirectionAwarePlacement(direction, placementProp === 'auto' ? 'top' : placementProp),
        middleware: [
            renderArrow && arrow({ element: arrowTipRef, padding: 4 }),
            // Disable shift middleware if crossAxis flipping is enabled
            // https://floating-ui.com/docs/flip#combining-with-shift
            shift(),
            offset({ mainAxis: (renderArrow ? ARROWUP_HEIGHT_PX : 0) + POPOVER_OFFSET_PX }),
            flip({
                mainAxis: true,
                crossAxis: false,
                fallbackPlacements: fallbackPlacements,
                fallbackStrategy: 'initialPlacement',
            }),
            hideWhenOutOfView && hide({ rootBoundary: boundariesElement }),
        ],
        strategy: positionStrategy,
        whileElementsMounted: autoUpdate,
    }), context = _r.context, middlewareData = _r.middlewareData, placement = _r.placement, refs = _r.refs, strategy = _r.strategy, x = _r.x, y = _r.y;
    var _s = useTransitionStyles(context, {
        duration: {
            open: 200,
            close: 130,
        },
        close: getClosedStyles,
        open: getOpenedStyles,
    }), isMounted = _s.isMounted, styles = _s.styles;
    var placementSide = placement.split('-')[0];
    useOnClickInside([refs.floating], handleClickInside);
    useOnClickOutside(isFloatingUiReferenceRegularElement(refs.reference)
        ? [refs.reference, refs.floating]
        : [refs.floating, refs.floating], // deps array length shall not change
    handleClickOutsideContentAndPositionRef);
    var popover = isMounted ? (React.createElement(PopoverBase, { arrowPosition: middlewareData.arrow, arrowTipRef: renderArrow ? arrowTipRef : undefined, onMouseEnter: popoverState.popoverHandlers.onMouseEnter, onMouseLeave: popoverState.popoverHandlers.onMouseLeave, ref: refs.setFloating, role: "dialog", side: placementSide, style: __assign(__assign(__assign({}, getClosedStyles({ side: placementSide })), styles), { opacity: ((_b = middlewareData.hide) === null || _b === void 0 ? void 0 : _b.referenceHidden) ? 0 : styles.opacity, position: strategy, top: y !== null && y !== void 0 ? y : '0px', left: x !== null && x !== void 0 ? x : '0px', zIndex: contentZIndex }) },
        placementSide === 'bottom' && (React.createElement(HoverArea, { style: {
                left: 3,
                right: 3,
                top: -HOVER_AREA_WIDTH,
                height: HOVER_AREA_WIDTH,
            } })),
        placementSide === 'top' && (React.createElement(HoverArea, { style: {
                left: 3,
                right: 3,
                bottom: -HOVER_AREA_WIDTH,
                height: HOVER_AREA_WIDTH,
            } })),
        placementSide === 'right' && (React.createElement(HoverArea, { style: {
                top: 3,
                bottom: 3,
                left: -HOVER_AREA_WIDTH,
                width: HOVER_AREA_WIDTH,
            } })),
        placementSide === 'left' && (React.createElement(HoverArea, { style: {
                top: 3,
                bottom: 3,
                right: -HOVER_AREA_WIDTH,
                width: HOVER_AREA_WIDTH,
            } })),
        React.createElement(Content, { "$padded": !disableContentPadding }, typeof content === 'function' ? content(renderProps) : content))) : null;
    return (React.createElement(React.Fragment, null,
        React.createElement(Root, __assign({}, popoverState.referenceHandlers, { className: className, ref: refs.setReference, style: style }), typeof children === 'function' ? children(renderProps) : children),
        portalElement !== null && typeof window === 'object' && window.document
            ? createPortal(popover, portalElement)
            : popover));
};
var templateObject_1, templateObject_2, templateObject_3;
//# sourceMappingURL=LegacyPopover.js.map